import { exportTMJ } from '../core/TMJExporter.js';

/**
 * GitHub integration — push/pull TMJ files via GitHub Contents API.
 */

const STORAGE_KEY = 'github_pat';

export class GitHubSync {
  constructor(state, fileManager) {
    this._state = state;
    this._fileManager = fileManager;
    this._secureStorage = null; // Set via setSecureStorage()
    this._token = localStorage.getItem(STORAGE_KEY) || null;
    this._repo = null;   // { owner, name, full_name }
    this._branch = null;
    this._path = '';      // Target directory path in the repo
    this._onStatusChange = [];
  }

  /** Bind SecureStorage for encrypted token storage. */
  setSecureStorage(secureStorage) {
    this._secureStorage = secureStorage;
    // Load token from secure storage (async — may overwrite sync-loaded plaintext)
    secureStorage.get(STORAGE_KEY).then(val => {
      if (val) this._token = val;
    }).catch(() => {});
  }

  /** Register a callback for connection status changes. */
  onStatusChange(fn) {
    this._onStatusChange.push(fn);
    return () => { this._onStatusChange = this._onStatusChange.filter(f => f !== fn); };
  }

  get isConnected() {
    return !!this._token && !!this._repo;
  }

  get token() {
    return this._token;
  }

  get repo() {
    return this._repo;
  }

  get branch() {
    return this._branch;
  }

  get path() {
    return this._path;
  }

  /** Set the PAT and save (encrypted if authenticated). */
  setToken(token) {
    this._token = token;
    if (this._secureStorage) {
      this._secureStorage.set(STORAGE_KEY, token).catch(() => {
        // Fallback to plaintext
        if (token) localStorage.setItem(STORAGE_KEY, token);
        else localStorage.removeItem(STORAGE_KEY);
      });
    } else {
      if (token) localStorage.setItem(STORAGE_KEY, token);
      else localStorage.removeItem(STORAGE_KEY);
    }
  }

  /** Set repo target. */
  setRepo(repo, branch, path) {
    this._repo = repo;
    this._branch = branch || 'main';
    this._path = path || '';
    this._notify();
  }

  disconnect() {
    this._repo = null;
    this._branch = null;
    this._path = '';
    this._notify();
  }

  /** List repos for authenticated user. */
  async listRepos() {
    const res = await this._fetch('https://api.github.com/user/repos?per_page=50&sort=updated');
    if (!res.ok) throw new Error(`Failed to list repos (${res.status})`);
    const repos = await res.json();
    return repos.map(r => ({ owner: r.owner.login, name: r.name, full_name: r.full_name, default_branch: r.default_branch }));
  }

  /** List branches for a repo. */
  async listBranches(owner, repoName) {
    const res = await this._fetch(`https://api.github.com/repos/${owner}/${repoName}/branches?per_page=50`);
    if (!res.ok) throw new Error(`Failed to list branches (${res.status})`);
    const branches = await res.json();
    return branches.map(b => b.name);
  }

  /** List files at a path in the repo. */
  async listFiles(dirPath) {
    if (!this._repo) throw new Error('No repo selected');
    const p = dirPath || this._path || '';
    const url = `https://api.github.com/repos/${this._repo.full_name}/contents/${p}?ref=${this._branch}`;
    const res = await this._fetch(url);
    if (!res.ok) {
      if (res.status === 404) return [];
      throw new Error(`Failed to list files (${res.status})`);
    }
    const items = await res.json();
    if (!Array.isArray(items)) return []; // It's a single file, not a directory
    return items.map(i => ({ name: i.name, path: i.path, type: i.type, sha: i.sha, size: i.size }));
  }

  /** Save (push) the current map as TMJ to the repo. */
  async save(filePath) {
    if (!this._repo) throw new Error('No repo selected');

    const tmj = exportTMJ(this._state);
    const json = JSON.stringify(tmj, null, 2);
    const content = btoa(unescape(encodeURIComponent(json)));

    const targetPath = filePath || this._buildPath();
    const url = `https://api.github.com/repos/${this._repo.full_name}/contents/${targetPath}`;

    // Check if file exists (need sha for update)
    let sha = null;
    try {
      const checkRes = await this._fetch(`${url}?ref=${this._branch}`);
      if (checkRes.ok) {
        const existing = await checkRes.json();
        sha = existing.sha;
      }
    } catch { /* file doesn't exist yet */ }

    const body = {
      message: `Update ${targetPath.split('/').pop()} via TileForge`,
      content,
      branch: this._branch,
    };
    if (sha) body.sha = sha;

    const res = await this._fetch(url, {
      method: 'PUT',
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `Failed to save (${res.status})`);
    }

    return await res.json();
  }

  /** Load (pull) a TMJ file from the repo. */
  async load(filePath) {
    if (!this._repo) throw new Error('No repo selected');

    const url = `https://api.github.com/repos/${this._repo.full_name}/contents/${filePath}?ref=${this._branch}`;
    const res = await this._fetch(url);
    if (!res.ok) throw new Error(`Failed to load file (${res.status})`);

    const data = await res.json();
    const content = decodeURIComponent(escape(atob(data.content)));
    return { content, name: data.name, path: data.path, sha: data.sha };
  }

  _buildPath() {
    const fileName = this._state.get('fileName') || 'untitled.tmj';
    return this._path ? `${this._path}/${fileName}` : fileName;
  }

  _fetch(url, options = {}) {
    return fetch(url, {
      ...options,
      headers: {
        'Accept': 'application/vnd.github.v3+json',
        'Authorization': `Bearer ${this._token}`,
        ...(options.headers || {}),
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
  }

  _notify() {
    for (const fn of this._onStatusChange) {
      try { fn(); } catch (e) { console.error('GitHub status listener error:', e); }
    }
  }
}
